import { useState, useCallback } from 'react';
import { RMMClient } from '../../../index';
import { COMMON_CONSTANTS } from '../../../index';
import {
  GENERATE_MDM_ENROLLMENT_QR,
  SEND_MDM_ENROLLMENT_QR_VIA_EMAIL
} from '../../../DeviceGraphQL/queries/MDMSettingsPage';
import {
  QRGenerationParams,
  EnrollmentResponse,
  PlatformType,
  DeviceType
} from '../types';
import { useActivityTracker } from '@nile/components';

export const useMDMEnrollment = (isMSPProduct: boolean = false) => {
  const [generateQRLoading, setGenerateQRLoading] = useState(false);
  const [sendEmailLoading, setSendEmailLoading] = useState(false);
  const [
    enrollmentResponse,
    setEnrollmentResponse
  ] = useState<EnrollmentResponse | null>(null);
  const [qrConfigData, setQrConfigData] = useState<string | null>(null);
  const [showQRCard, setShowQRCard] = useState(false);
  const [qrLoading, setQrLoading] = useState(true);

  // Helper function to validate required fields based on product type
  const isValidationFailed = useCallback(
    (client: any, siteId: string) => {
      if (isMSPProduct) {
        // MSP: Both client?.accountId and siteId are mandatory
        return !client?.accountId || !siteId;
      } else {
        // ITSM: Only siteId is mandatory
        return !siteId;
      }
    },
    [isMSPProduct]
  );

  const getClassId = useCallback(
    (activeView: PlatformType, selectedDevice: DeviceType): string => {
      if (activeView === 'ANDROID') {
        return '41'; // Android
      }
      if (activeView === 'IOS') {
        switch (selectedDevice) {
          case 'iPhone':
            return '42'; // Apple iOS
          case 'iPad':
            return '43'; // Apple iPadOS
          case 'Mac':
            return '3'; // Apple macOS
          default:
            return '42'; // Default to iPhone
        }
      }
      return '41'; // Default to Android
    },
    []
  );

  const generateEnrollmentQR = useCallback(
    async (params: QRGenerationParams) => {
      const {
        activeView,
        selectedDevice,
        enrollmentType,
        selectedPolicyCategory,
        selectedRequestor,
        client,
        siteId,
        selectedDepartment
      } = params;

      if (isValidationFailed(client, siteId)) {
        return;
      }

      setGenerateQRLoading(true);
      try {
        const enrollmentTypeMap = {
          'fully-managed': 'FULLY_MANAGED',
          BYOD: 'BYOD',
          KIOSK: 'KIOSK'
        };

        const classId = getClassId(activeView, selectedDevice);

        const response = await RMMClient.query({
          query: GENERATE_MDM_ENROLLMENT_QR,
          fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE,
          variables: {
            input: {
              clientId: client.accountId,
              siteId: siteId,
              classId: classId,
              enrollmentType:
                activeView !== 'IOS'
                  ? enrollmentTypeMap[
                      enrollmentType[0] as keyof typeof enrollmentTypeMap
                    ] || 'FULLY_MANAGED'
                  : null,
              deviceCategoryId: selectedPolicyCategory,
              requesterId: selectedRequestor?.value,
              ...(selectedDepartment && {
                departmentId: selectedDepartment.businessFunctionId
              })
            }
          }
        });

        if (response?.data?.generateMDMEnrollmentQR) {
          const enrollmentData = response.data.generateMDMEnrollmentQR;
          setEnrollmentResponse(enrollmentData);

          // Handle QR code generation from enrollment configuration
          if (enrollmentData.qrCode) {
            try {
              useActivityTracker({
                event: `MDM - Manual Enrollment - Generate QR - ${activeView}`
              });
              // Decode the URL-encoded JSON
              const decodedConfig = decodeURIComponent(enrollmentData.qrCode);

              // Set the config data for QR code generation
              setQrConfigData(decodedConfig);
              setShowQRCard(true);
              setQrLoading(false);
            } catch (error) {
              setQrLoading(false);
            }
          } else {
            setQrLoading(false);
          }
        }
      } catch (error) {
        setQrLoading(false);
      } finally {
        setGenerateQRLoading(false);
      }
    },
    [getClassId, isValidationFailed]
  );

  const sendQRViaEmail = useCallback(
    async (params: QRGenerationParams, emailAddresses: string[]) => {
      const {
        activeView,
        selectedDevice,
        enrollmentType,
        selectedPolicyCategory,
        selectedRequestor,
        client,
        siteId,
        selectedDepartment
      } = params;

      if (
        emailAddresses.length === 0 ||
        !qrConfigData ||
        isValidationFailed(client, siteId)
      ) {
        return false;
      }

      setSendEmailLoading(true);
      try {
        const enrollmentTypeMap = {
          'fully-managed': 'FULLY_MANAGED',
          BYOD: 'BYOD',
          KIOSK: 'KIOSK'
        };

        const response = await RMMClient.query({
          query: SEND_MDM_ENROLLMENT_QR_VIA_EMAIL,
          fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE,
          variables: {
            input: {
              clientId: client.accountId,
              siteId: siteId,
              classId: getClassId(activeView, selectedDevice),
              enrollmentType:
                enrollmentTypeMap[
                  enrollmentType[0] as keyof typeof enrollmentTypeMap
                ] || 'FULLY_MANAGED',
              deviceCategoryId: selectedPolicyCategory,
              requesterId: selectedRequestor?.value,
              ...(selectedDepartment && {
                departmentId: selectedDepartment.businessFunctionId
              })
            },
            emailIds: emailAddresses
          }
        });

        if (response?.data?.sendMDMEnrollmentQRViaEmail) {
          useActivityTracker({
            event: `MDM - Sent QR via email - ${activeView}`
          });
          return true;
        }
        return false;
      } catch (error) {
        return false;
      } finally {
        setSendEmailLoading(false);
      }
    },
    [getClassId, qrConfigData, isValidationFailed]
  );

  const resetQRDisplay = useCallback(() => {
    setShowQRCard(false);
    setQrConfigData(null);
    setEnrollmentResponse(null);
    setQrLoading(true);
  }, []);

  return {
    generateQRLoading,
    sendEmailLoading,
    enrollmentResponse,
    qrConfigData,
    showQRCard,
    qrLoading,
    generateEnrollmentQR,
    sendQRViaEmail,
    setQrLoading,
    resetQRDisplay
  };
};
